<template>
  <div class="main-page-container">
    <AlertFoundActiveSession />

    <b-modal
      id="announcement-data-modal"
      size="lg"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      :scrollable="true"
      ref="announcement-data-modal"
    >
      <viewAnnouncementData />
    </b-modal>

    <b-modal id="addAnnouncementACL" hide-header hide-footer>
      <addAnnouncementACL />
    </b-modal>

    <b-modal
      id="deleteAnnouncementConfirmModal"
      size="lg"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      :scrollable="true"
      ref="deleteAnnouncementConfirmModal"
    >
      <deleteAnnouncementModal
        @deleteAnnouncementEvent="fetchAllAnnouncementsData"
      />
    </b-modal>
    <div class="d-flex">
      <PageHeader
        :title="title"
        :hasHeaderButton="true"
        class="page-top-header d-flex align-items-center vp-width-270"
      >
        <template #headerButton>
          <button class="cursor-pointer" @click="redirectToAddAnnouncement()">
            {{ $t("common.add") }}
          </button>
        </template>
      </PageHeader>
    </div>

    <!-- grid -->
    <section
      class="announcements-table announcements-white-bg-sec"
      style="background-color: white"
    >
      <DxDataGrid
        id="gridAnnouncementsData"
        :data-source="announcements"
        :ref="dataGridRefName"
        :remote-operations="false"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        :two-way-binding-enabled="true"
        :no-data-text="gridEmptyMessage"
        :class="{
          announcementGridHeightWithAlert: getActiveSessionsCount,
          announcementGridHeight: !getActiveSessionsCount,
        }"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
      >
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />
        <DxPaging :page-size="50" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="[5, 10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />

        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxFilterRow :visible="true" />

        <DxHeaderFilter :visible="true" />

        <DxColumnChooser
          :enabled="true"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
        </DxToolbar>

        <DxColumn
          data-field="title"
          :caption="$t('common.title')"
          :allow-header-filtering="false"
          :allow-filtering="true"
          :filter-operations="allowedOperations"
          :visible="true"
          :width="200"
          data-type="string"
        />

        <DxColumn
          data-field="createdBy"
          :caption="$t('announcements.grid.createdBy')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="true"
          :allow-header-filtering="false"
        />

        <DxColumn
          data-field="createdAt"
          :caption="$t('announcements.grid.createdAt')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="true"
          :allow-header-filtering="false"
          data-type="string"
        />

        <DxColumn
          data-field="user_types"
          :caption="$t('announcements.form.userType')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="true"
          :allow-header-filtering="false"
          data-type="string"
          :width="300"
          css-class="userTypesAnnouncementsColumn"
        />

        <DxColumn
          data-field="has_areas"
          :caption="$t('common.area')"
          :allow-editing="false"
          :visible="true"
          :allow-filtering="true"
          :allow-header-filtering="false"
          data-type="string"
          :width="300"
        />

        <DxColumn
          data-field="verbal"
          :caption="$t('announcements.form.verbal')"
          :allow-editing="false"
          :visible="true"
          :allow-filtering="true"
          :allow-header-filtering="true"
          data-type="string"
          :width="150"
        />

        <DxColumn
          data-field="status"
          :caption="$t('common.status')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="false"
          :allow-header-filtering="true"
          data-type="string"
          cell-template="status-cell"
        />

        <DxColumn
          data-field="publish_time"
          :caption="$t('announcements.grid.publishTime')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          data-type="string"
        />

        <DxColumn
          data-field="expiry_time"
          :caption="$t('announcements.grid.expiryTime')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          data-type="string"
        />

        <DxColumn
          data-field="urgent"
          :caption="$t('announcements.urgent')"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          :visible="true"
          :allow-filtering="false"
          data-type="string"
        />

        <DxColumn
          data-field="pinned"
          :caption="$t('announcements.pinned')"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          :visible="true"
          :allow-filtering="false"
          data-type="string"
        />

        <DxColumn
          data-field="editBy"
          :caption="$t('announcements.grid.lastEditBy')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="true"
          :allow-header-filtering="false"
          data-type="string"
        />

        <DxColumn
          data-field="editAt"
          :caption="$t('announcements.grid.lastEditAt')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="true"
          :allow-header-filtering="false"
          data-type="string"
        />

        <DxColumn
          data-field="actions"
          :caption="$t('common.grid.actions')"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          :visible="true"
          :allow-sorting="false"
          cell-template="action-cell"
          data-type="object"
        />

        <template #status-cell="{ data }">
          <span
            :class="
              data.data.is_active
              ? 'active-announcements'
                : data.data.is_scheduled
                ? 'scheduled-announcements'
                : 'expire-announcements'
            "
            >{{ data.data.status }}</span
          >
        </template>

        <template #action-cell="{ data }">
          <ActionCell :announcementData="data.data" />
        </template>

        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            :text="$t('common.grid.export')"
            @item-click="onItemClick"
          />
        </template>

        <template #columnButtonTemplate>
          <button class="custom-columns" @click="dataGrid.showColumnChooser()">
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>
            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="reset()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>
      </DxDataGrid>
    </section>
  </div>
</template>

<script>
const dataGridRefName = "announcementsDataGrid";
import { mapActions, mapGetters } from "vuex";
import PageHeader from "../../../components/page-header.vue";
import ActionCell from "@/components/announcements/ActionCell";
import viewAnnouncementData from "@/components/announcements/viewAnnouncementData";
import addAnnouncementACL from "@/router/views/announcements/modals/AddAnnouncementACL";
import deleteAnnouncementModal from "@/components/announcements/deleteAnnouncementModal";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import { announcementsColumns } from "@/utils/dataSource";
import "jspdf-autotable";
import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import moment from "moment";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

import DxDataGrid, {
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxColumnChooser,
  DxToolbar,
  DxItem,
  DxStateStoring,
  DxScrolling,
  DxPaging,
  DxPager,
} from "devextreme-vue/data-grid";

export default {
  name: "announcements",
  title: "Announcements",
  components: {
    PageHeader,
    deleteAnnouncementModal,
    viewAnnouncementData,
    ActionCell,
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    DxToolbar,
    DxItem,
    DxDropDownButton,
    DxStateStoring,
    Button,
    DxScrolling,
    DxPaging,
    DxPager,
    addAnnouncementACL,
    AlertFoundActiveSession,
  },
  data() {
    return {
      gridEmptyMessage : this.$t('common.grid.loadingData'),
      // title: "Announcements",
      dataGridRefName,
      announcements: [],
      isLoading: true,
      announcementsColumns: announcementsColumns,
      allowedOperations: ["contains"],
      selectedOperation: "contains", // default icon
      buttonData: ["Excel", "CSV", "PDF"],
      showClearAll: false,
      hasPermittedUserType: null,
      rtlEnabled: false,
    };
  },
  computed: {
    ...mapGetters("authfack", ["userDetails", "currentEntity"]),
    ...mapGetters("grid", ["getTableHeaders", "getTableFilters"]),
    ...mapGetters("emergency", ["getActiveSessionsCount"]),
    ...mapGetters("i18N", ["getLanguage"]),
    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    title() {
      return this.$t("sideNavbarMenu.announcements.text");
    },
  },
  watch: {
    getTableHeaders(value) {
      console.log("watch", value);
    },
    isLoading(newVal) {
      // Force update to ensure the correct rendering of no-data-text
      if (!newVal) {
        this.$forceUpdate();
      }
    },
  },
  created() {
    document.title = this.$t("sideNavbarMenu.announcements.text");
    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });
  },
  methods: {
    ...mapActions("userType", [
      "fetchAllPreRegistrationByAdminUserTypesAndPermittedNormalAreas",
    ]),
    ...mapActions("announcements", ["fetchAllAnnouncements"]),
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "createTableHeaders",
      "onSetLoading",
    ]),
    ...mapActions("modal", ["setData"]),
    async redirectToAddAnnouncement() {
      if (!this.hasPermittedUserType) {
        this.$bvModal.show("addAnnouncementACL");
        return;
      }
      this.$router.push({ path: `/add-announcement` });
    },
    goToAnnouncementDetails(announcement) {
      this.$router.push({
        path: "/announcement-details",
        query: {
          id: announcement.id,
          title: announcement.title,
        },
      });
    },
    fetchAllAnnouncementsData() {
      // console.log('deleted');
      this.gridEmptyMessage = this.$t('common.grid.loadingData');
      this.gridLoader();
      this.announcements = [];
      this.isLoading = true;
      let currentDateTime = new Date();
      // console.log('currentDateTime',currentDateTime);
      this.fetchAllAnnouncements().then((data) => {
        // console.log('data', data);
        if (data && data.length) {
          data.map((announcement) => {
            let publish_date = new Date(announcement.publish_date);
            let expiry_date = announcement.expiry_date
              ? new Date(announcement.expiry_date)
              : null;

            if (publish_date > currentDateTime) {
              announcement.status  =this.$t('announcements.grid.scheduled');
              announcement.is_scheduled = true;
            } else if (
              (publish_date < currentDateTime &&
                expiry_date > currentDateTime) ||
              !expiry_date
            ) {
              announcement.status = this.$t('announcements.grid.active');
              announcement.is_active = true;
            } else if (expiry_date < currentDateTime) {
              announcement.status = this.$t('announcements.grid.expired');
              announcement.is_expire = true;
            }
            announcement.user_types =
              announcement.userTypes && announcement.userTypes.length
                ? announcement.userTypes.join(" - ")
                : "-";
            announcement.has_areas =
              announcement.areas && announcement.areas.length
                ? announcement.areas.join(" - ")
                : "-";
            announcement.verbal =
              announcement.areas && announcement.areas.length
                ? announcement.is_verbal
                  ? this.$t('common.yes')
                  : this.$t('common.no')
                : "-";
            announcement.createdBy = `${this.$t('common.admin')} - ${announcement.created_by}`;
            announcement.createdAt = moment(announcement.created_at).format(
              "DD/MM/YYYY - hh:mm A"
            );
            announcement.publish_time = moment(
              announcement.publish_date
            ).format("DD/MM/YYYY - hh:mm A");
            announcement.expiry_time = announcement.expiry_date
              ? moment(announcement.expiry_date).format("DD/MM/YYYY - hh:mm A")
              : this.$t("announcements.form.never");
            announcement.urgent = announcement.urgent ? this.$t('common.yes') : this.$t('common.no');
            announcement.pinned = announcement.is_pinned ? this.$t('common.yes') : this.$t('common.no');
            announcement.editAt = announcement.updated_at
              ? moment(announcement.updated_at).format("DD/MM/YYYY - hh:mm A")
              : "-";
            announcement.editBy = announcement.updated_by
              ? `${this.$t('common.admin')} - ${announcement.updated_by}`
              : "-";
            // announcement.data = announcement;
            this.announcements.push(announcement);
          });
        }
        this.gridEmptyMessage = this.$t('common.grid.noRecordsYet');
        this.isLoading = false;
        this.dataGrid.endCustomLoading();
      });
    },
    async customLoad() {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/announcements-grid-data",
        });
        let value = this.getTableHeaders;
        console.log("customLoad");
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 50;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");
        if (!this.getTableHeaders) {
          console.log("createTableHeaders");
          this.createTableHeaders({
            url: "/announcements-grid-data",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          console.log("updateTableHeaders");
          this.updateTableHeaders({
            url: "/announcements-grid-data",
            headers: value,
            filters: "[]",
          });
        }
        console.log(
          "customSave value",
          value != JSON.stringify(this.getTableHeaders)
        );
      }
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/announcements-grid-data",
        headers: announcementsColumns,
      });
    },
    onToggleVisible() {
      this.$bvModal.show("announcementsColumns");
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    async onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");
        await exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: ({ gridCell, excelCell }) => {
            // const {gridCell, excelCell} = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
            if (gridCell.column.dataField === "createdBy") {
              if (gridCell.value) {
                gridCell.value = gridCell.value.replace(/[\u202A-\u202E\u200E\u200F\u2066-\u2069\uFEFF]/g, "");
                excelCell.value = gridCell.value;
              }
            }
          },
        }).then(function () {
          workbook.xlsx.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "announcements.xlsx"
            );
          });
        });
        // e.cancel = true;
      } else if (e.itemData == "CSV") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        await exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: ({ gridCell, excelCell }) => {
            if (gridCell.column.dataField === "createdBy") {
              if (gridCell.value) {
                gridCell.value = gridCell.value.replace(/[\u202A-\u202E\u200E\u200F\u2066-\u2069\uFEFF]/g, "");
                excelCell.value = gridCell.value;
              }
            }
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.csv.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "announcements.csv"
            );
          });
        });
      } else if (e.itemData == "PDF") {
        const doc = new jsPDF('landscape');

        // Add custom font
        doc.addFileToVFS("Amiri-Regular.ttf", amiriFont);
        doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");

        doc.autoTable({ direction: "rtl" });

        await exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.dataGrid,
          // showHead: "everyPage",
          customizeCell: function (options) {
            const { gridCell, pdfCell } = options;

            if (gridCell.rowType === "data") {
              if (gridCell.column.dataField === "createdBy") {
                // for (let i = 0; i < gridCell.value.length; i++) {
                //   console.log(`Character: ${gridCell.value[i]}, Code: ${gridCell.value.charCodeAt(i)}`);
                // }
                gridCell.value = gridCell.value.replace(/[\u202A-\u202E\u200E\u200F\u2066-\u2069\uFEFF]/g, "");
                pdfCell.text = gridCell.value;
                pdfCell.content = gridCell.value;
              }

              // data columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                cellWidth: "auto",
                halign: "center",
              };
            } else {
              // header columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                fillColor: "#2680EB",
                textColor: "#FFFFFF",
                cellWidth: "auto",
                halign: "center", 
              };
            }
          },
          customizeTable: (doc) => {
            doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
          },
        }).then(() => {
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageSize = doc.internal.pageSize;
            const pageWidth = pageSize.width
              ? pageSize.width
              : pageSize.getWidth();
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            const header = this.$t("sideNavbarMenu.announcements.text")
            const headerWidth = doc.getTextDimensions(header).w;
            const footer = `${this.$t("common.page")} ${i} ${this.$t("common.of")} ${pageCount}`;

            // Header
            doc.text(header, (pageWidth - headerWidth) / 2, 10);

            // Footer
            doc.text(
              footer,
              pageWidth / 2 - doc.getTextWidth(footer) / 2,
              pageHeight - 5,
              { baseline: "bottom" }
            );
          }

          doc.save("announcements.pdf");
        });
      }
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
    },
    gridLoader() {
      setTimeout(() => {
        this.dataGrid.beginCustomLoading();
      }, 100);
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-datagrid-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
  async beforeMount() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    this.fetchAllAnnouncementsData();
    await this.fetchAllPreRegistrationByAdminUserTypesAndPermittedNormalAreas({
      method: "PRE_REGISTERED_BY_ADMIN",
      type: "create",
    }).then((data) => {
      console.log(data.data);
      
      this.hasPermittedUserType =
        data.data.user_types?.length || data.data.sites?.length ? true : false;
    });
  },
};
</script>

<style lang="scss">
.announcementGridHeight .dx-datagrid {
  height: calc(100vh - 197px);
}
.announcementGridHeightWithAlert .dx-datagrid {
  height: calc(100vh - 243px);
}
button.btn.d-flex.align-items-center.justify-content-center.text-center.add-announcement-btn.btn-primary.btn-sm.rounded-pill {
  margin-inline-start: 40px;
  height: 27px;
  width: 127px;

  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #ffffff;
}

.announcementsGrid {
  min-height: 693px !important;
  background-color: white;
  padding: 38px 38px 38px 38px;
  border-radius: 8px;

  .no-announcements-message {
    margin-top: 300px;
    color: #495057;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
  }

  .announcementsCard {
    margin-bottom: 16px;
    padding: 24px 24px 24px 24px;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    border-radius: 5px;
    background: #f6f6f6;
  }

  .announcement-title {
    color: #495057;
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
  }

  .d-flex.announcemet-disc {
    width: 100%;
    word-break: break-all;
  }
  .announcement-descriptionn {
    color: #495057;
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 16px;
    line-height: 24px;
  }

  .active-announcements {
    color: var(--34-c-38-f, #34c38f);

    font-size: 16px;

    font-weight: 700;
    line-height: 24px;
    margin-bottom: 0px;
  }

  .expire-announcements {
    color: var(--Error, #f46a6a);

    font-size: 16px;

    font-weight: 700;
    margin-bottom: 0px;
    line-height: 24px;
  }

  .scheduled-announcements {
    color: var(--2680-eb, #2680eb);

    font-size: 16px;

    font-weight: 700;
    margin-bottom: 0px;
    line-height: 24px;
  }

  .ml-29 {
    margin-inline-start: 29px;
  }

  .mt-60 {
    margin-top: 37px;
  }

  .spinner-announcements {
    margin-top: 200px !important;
    margin-inline-end: 600px !important;
    margin-bottom: 200px !important;
    margin-inline-start: 700px !important;
  }
}

div#gridAnnouncementsData {

  .active-announcements {
    color: var(--34-c-38-f, #34c38f);

    font-size: 16px;

    font-weight: 700;
    line-height: 24px;
    margin-bottom: 0px;
  }

  .expire-announcements {
    color: var(--Error, #f46a6a);

    font-size: 16px;

    font-weight: 700;
    margin-bottom: 0px;
    line-height: 24px;
  }

  .scheduled-announcements {
    color: var(--2680-eb, #2680eb);

    font-size: 16px;

    font-weight: 700;
    margin-bottom: 0px;
    line-height: 24px;
  }

  .userTypesAnnouncementsColumn {
    white-space: break-spaces;
  }
}

.announcements-white-bg-sec {
  background-color: white;
  padding-inline-start: 0px;
  border-radius: 8px !important;
}

.announcements-table {
  tbody {
    color: #495057;
  }
}
</style>
